/**
 * Produces a short plain-text snippet from community post HTML (or DB LEFT(content)).
 * Strips embeds (YouTube wrappers, iframes), images, scripts, then tags — for chat/search previews.
 */
export function plainTextSnippetFromHtml(
  html: string | null | undefined,
  options?: { maxLength?: number }
): string | null {
  if (!html || !String(html).trim()) return null;
  let s = String(html);
  s = s.replace(/<script[\s\S]*?<\/script>/gi, ' ');
  s = s.replace(/<style[\s\S]*?<\/style>/gi, ' ');
  s = s.replace(/<div\b[^>]*data-youtube-video[^>]*>[\s\S]*?<\/div>/gi, ' ');
  s = s.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, ' ');
  s = s.replace(/<img[^>]*>/gi, ' ');
  s = s.replace(/<\/(p|div|li|h[1-6]|blockquote|pre)>/gi, '\n');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<[^>]+>/g, ' ');
  s = s.replace(/&nbsp;/gi, ' ');
  s = s.replace(/&#x0*20;/gi, ' ');
  s = s.replace(/&#0*32;/g, ' ');
  s = s.replace(/<[^>]*$/, '');
  s = s.replace(/^[^<]*>/, '');
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) return null;
  const max = options?.maxLength ?? 200;
  if (s.length <= max) return s;
  return `${s.slice(0, max).trimEnd()}…`;
}

/**
 * Named entities TipTap leaves in a description. `plainTextSnippetFromHtml` only
 * turns space entities into spaces; callers that print the result as plain text
 * (digest subject, digest body snippet) decode the rest here.
 *
 * One pass, and replacements are not rescanned, so `&amp;quot;` stays `&quot;`.
 */
const PLAIN_TEXT_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  mdash: '—',
  ndash: '–',
  hellip: '…',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
};

/** Numeric and named entities, then collapsed so `&#160;` still separates words. */
export function decodePlainTextEntities(text: string): string {
  const decoded = text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, token: string) => {
    const lower = token.toLowerCase();
    if (lower.startsWith('#')) {
      const hex = lower[1] === 'x';
      const value = Number.parseInt(hex ? lower.slice(2) : lower.slice(1), hex ? 16 : 10);
      // Lone surrogates and out-of-range values are not characters; leave them encoded.
      if (!Number.isInteger(value) || value < 0 || value > 0x10ffff || (value >= 0xd800 && value <= 0xdfff)) {
        return entity;
      }
      // Controls are whitespace, not words and not header bytes.
      if (value < 0x20 || value === 0x7f) return ' ';
      return String.fromCodePoint(value);
    }
    // Own-property only: `&constructor;` matches the pattern and would otherwise resolve
    // up the prototype chain to a function. `hasOwnProperty.call` rather than
    // `Object.hasOwn` — shared's TS target lib is ES2020.
    if (!Object.prototype.hasOwnProperty.call(PLAIN_TEXT_HTML_ENTITIES, lower)) return entity;
    return PLAIN_TEXT_HTML_ENTITIES[lower] ?? entity;
  });
  return decoded.replace(/\s+/g, ' ').trim();
}

const ROW_LABEL_MAX_LENGTH = 100;

/**
 * Label for a list row that structurally needs one. Community post titles are
 * optional and stored as '', so a titleless post falls back to the opening
 * words of its body. Returns null when neither exists — the caller drops the
 * label instead of rendering a blank one.
 */
export function postRowLabel(
  title: string | null | undefined,
  content?: string | null,
  options?: { maxLength?: number }
): string | null {
  const trimmed = title?.trim();
  if (trimmed) return trimmed;
  return plainTextSnippetFromHtml(content, { maxLength: options?.maxLength ?? ROW_LABEL_MAX_LENGTH });
}
